// Permission utilities for role-based access control


// Map of actions to their required permissions
export const PERMISSIONS = {
  // Client Management
  CREATE_CLIENT: 'CREATE_CLIENT',
  DELETE_CLIENT: 'DELETE_CLIENT',
  EDIT_CLIENT: 'EDIT_CLIENT',
  VIEW_CLIENT: 'VIEW_CLIENT',
  
  // Trade Partner Management
  MANAGE_TRADE_PARTNER: 'MANAGE_TRADE_PARTNER',
  VIEW_TRADE_PARTNER: 'VIEW_TRADE_PARTNER',
  
  // Commission
  CREATE_COMMISSION: 'CREATE_COMMISSION',
  DOWNLOAD_COMMISSION: 'DOWNLOAD_COMMISSION',
  VIEW_COMMISSION: 'VIEW_COMMISSION',
  
  // Decommission
  CREATE_DECOMMISSION: 'CREATE_DECOMMISSION',
  VIEW_DECOMMISSION: 'VIEW_DECOMMISSION',
  
  // Dispensing
  DISPENSE_LABEL: 'DISPENSE_LABEL',
  VIEW_DISPENSING: 'VIEW_DISPENSING',
  
  // Labels
  CHECK_LABEL: 'CHECK_LABEL',
  VIEW_LABEL: 'VIEW_LABEL',
  VIEW_LABEL_TABLE: 'VIEW_LABEL_TABLE',
  
  // Packing
  PACK_LABEL: 'PACK_LABEL',
  DOWNLOAD_PACKING: 'DOWNLOAD_PACKING',
  VIEW_PACKING: 'VIEW_PACKING',
  
  // Receiving
  RECEIVE_LABEL: 'RECEIVE_LABEL',
  VIEW_RECEIVING: 'VIEW_RECEIVING',
  
  // Shipping
  SHIP_LABEL: 'SHIP_LABEL',
  VIEW_SHIPPING: 'VIEW_SHIPPING',
  
  // Unpacking
  UNPACK_LABEL: 'UNPACK_LABEL',
  VIEW_UNPACKING: 'VIEW_UNPACKING',
  
  // Location
  CREATE_LOCATION: 'CREATE_LOCATION',
  DELETE_LOCATION: 'DELETE_LOCATION',
  EDIT_LOCATION: 'EDIT_LOCATION',
  VIEW_LOCATION: 'VIEW_LOCATION',
  
  // User Management
  CREATE_USER: 'CREATE_USER',
  DELETE_USER: 'DELETE_USER',
  EDIT_USER: 'EDIT_USER',
  VIEW_USER: 'VIEW_USER',
  MANAGE_USER_CLIENTS: 'MANAGE_USER_CLIENTS',
  MANAGE_USER_ROLES: 'MANAGE_USER_ROLES',
  
  // Role Management
  CREATE_ROLE: 'CREATE_ROLE',
  DELETE_ROLE: 'DELETE_ROLE',
  EDIT_ROLE: 'EDIT_ROLE',
  VIEW_ROLE: 'VIEW_ROLE',
  MANAGE_ROLE_PERMISSIONS: 'MANAGE_ROLE_PERMISSIONS',
};

// User roles
export const ROLES = {
	SUPER_ADMIN: "SUPER_ADMIN",
	TRADING_PARTNER: "TRADING_PARTNER",
	ADMIN: "ADMIN",
	USER: "USER",
	INFORMER_MOBILE: "INFORMER_MOBILE",
};



// Check if user has a specific permission
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return userPermissions.includes(requiredPermission);
};


// Check if user has all of the specified permissions
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) {
    return false;
  }
  return requiredPermissions.every(permission => 
    userPermissions.includes(permission)
  );
};


// Check if user has any of the specified permissions
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) {
    return false;
  }
  return requiredPermissions.some(permission => 
    userPermissions.includes(permission)
  );
};


// Check if user role requires client selection
export const requiresClientSelection = (role) => {
  return role === ROLES.SUPER_ADMIN || role === ROLES.TRADING_PARTNER;
};


// Check if user can perform action (has permission and client selected if required)
export const canPerformAction = ({
  userPermissions,
  userRole,
  selectedClientId,
  requiredPermission,
}) => {
  // Check if user has the required permission
  if (!hasPermission(userPermissions, requiredPermission)) {
    return false;
  }

  // Check if client selection is required and has been done
  if (requiresClientSelection(userRole) && !selectedClientId) {
    return false;
  }

  return true;
};


// Get user-friendly error message for why action is disabled
export const getActionDisabledReason = ({
  userPermissions,
  userRole,
  selectedClientId,
  requiredPermission,
}) => {
  if (!hasPermission(userPermissions, requiredPermission)) {
    return 'You do not have permission to perform this action';
  }

  if (requiresClientSelection(userRole) && !selectedClientId) {
    return 'Please select a client to continue';
  }

  return null;
};